import { priceCents, safeColor } from '../cms-market-helpers.js';

const DAY = 86400000;

// Chart API date-only / timezone-less daily buckets are treated as UTC calendar dates.
function historyTime(value) {
  if (typeof value !== 'string' || !value) return NaN;
  return Date.parse(
    /T.*(?:Z|[+-]\d\d:\d\d)$/i.test(value) ? value : `${value.replace(/T.*$/, '')}T00:00:00Z`,
  );
}

export function buildSummary(tile, records, now = Date.now()) {
  const today = new Date(now);
  const start =
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()) - 30 * DAY;
  const sides = [tile.left, tile.right];
  const known = sides.every((side) => side.price !== null);
  const tied = known && tile.left.price === tile.right.price;
  const leader =
    known && !tied ? (tile.left.price > tile.right.price ? tile.left : tile.right) : null;
  const trailer = leader ? sides.find((side) => side.id !== leader.id) : null;
  const series = sides.map((side) => {
    const unique = new Map();
    for (const record of Array.isArray(records) ? records : []) {
      if (Number(record.contractId) !== side.id) continue;
      const time = historyTime(record.date);
      if (!Number.isFinite(time) || time < start || time > now) continue;
      unique.set(time, { time, price: priceCents(record.closeSharePrice) });
    }
    const historical = [...unique.values()].sort((a, b) => a.time - b.time);
    const validHistory = historical.filter((point) => point.price !== null);
    const baseline =
      validHistory.filter((point) => point.time <= now - 7 * DAY).at(-1) || validHistory[0];
    const weeklyChange =
      validHistory.length >= 2 && side.price !== null ? side.price - baseline.price : null;
    const hasHistory = validHistory.length > 0;
    // Live data is an overlay, not a mutation of the fetched daily-close snapshot.
    const points = historical.filter((point) => point.time < now);
    if (side.price !== null) points.push({ time: now, price: side.price });
    return {
      ...side,
      current: side.price,
      weeklyChange,
      hasHistory,
      points,
    };
  });
  return {
    start,
    now,
    series,
    leader,
    trailer,
    tied,
    verb: known
      ? tied
        ? 'tied'
        : Math.abs(tile.left.price - tile.right.price) < 5
          ? 'edges'
          : 'leads'
      : null,
    difference: known
      ? `${Math.abs(tile.left.price - tile.right.price)}${tile.currentPriceSymbol}`
      : '',
  };
}

// Preserve the CMS hue, moving only toward black/white when needed for AA text.
export function readableContractColor(value, dark) {
  const channels = safeColor(value)
    .slice(1)
    .match(/../g)
    .map((part) => parseInt(part, 16));
  const luminance = (rgb) =>
    rgb
      .map((v) => {
        const n = v / 255;
        return n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
      })
      .reduce((sum, n, i) => sum + n * [0.2126, 0.7152, 0.0722][i], 0);
  const background = luminance(dark ? [37, 50, 64] : [255, 255, 255]);
  let result = channels;
  for (let step = 0; step <= 20; step += 1) {
    result = channels.map((n) => Math.round(n + (((dark ? 255 : 0) - n) * step) / 20));
    const light = luminance(result);
    if ((Math.max(light, background) + 0.05) / (Math.min(light, background) + 0.05) >= 4.5) break;
  }
  return `#${result.map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}
